import { useState, useMemo, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { VIEW_TYPE } from '@constants/microsites';
import { ModalBase } from '@components/Modal';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { useTranslation, useYupValidationResolver } from '@hooks';
import PreviewMicrosite from '../PreviewMicrosite';
import General from './General';
import Landing from './Landing';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: 'calc(100% - 32px)',
        height: 'calc(var(--100vh) - 32px)',
        '@media (max-width: 600px)': {
            height: 'var(--100vh)',
            width: '100%',
        },
    },
    '& .modal-base-content': {
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '100%',
        },
    },
});

const FormWrapper = styled('div')({
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
        overflowX: 'initial',
    },
});

export const PreviewWrapper = styled('div')({
    backgroundColor: '#f2f2f2',
    overflowY: 'auto',
    padding: '24px 0',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
    },
    '@media (max-width: 420px)': {
        padding: '24px 16px',
    },
});

const RadioButtonsWrapper = styled('div')({
    overflowX: 'initial',
    height: 32,
    marginBottom: 24,
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    '@media (max-width: 768px)': {
        overflowX: 'auto',
    },
});

const RadioButtons = styled(RadioButtonsGroup)({
    width: '100%',
    display: 'block',
    marginBottom: '24px',
    '& .MuiFormControlLabel-root': {
        height: '32px',
        padding: '0 4px',
    },
});

const Tab = {
    Settings: 0,
    Content: 1,
};

const EditModal = ({ modal, onClose, selectedMicrosite, onDelete, onSave, account, isUser }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(Tab.Settings);

    const MicrositesSchema = yup.object().shape({
        name: yup.string().required('validation.required'),
        slug: yup
            .string()
            .required('validation.required')
            .not(selectedMicrosite?.restrictedNames || [], 'validation.takenURL')
            .matches(/^(?!unsubscribe+\b)/i, 'validation.takenURL')
            .matches(/^(?!q\/)/i, 'validation.takenURL')
            .matches(/^(?!s\/)/i, 'validation.takenURL')
            .matches(/^(?!c\/)/i, 'validation.takenURL')
            .min(8, 'validation.minLength8'),
        showLiveChat: yup.boolean(),
        liveChatChannelId: yup.string().when('showLiveChat', {
            is: true,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
    });
    const resolver = useYupValidationResolver(MicrositesSchema);

    const {
        setValue,
        control,
        watch,
        handleSubmit,
        getValues,
        formState: { isValid, isDirty, isSubmitting, errors },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            name: '',
            locationId: '',
            slug: '',
            socialMediaEnable: false,
            logoUrl: '',
            links: {},
            showReviews: false,
            showLiveChat: false,
            liveChatChannelId: '',
            subscriptionEnabled: false,
            subscriptionEmailEnabled: true,
            subscriptionEmailRequired: true,
            subscriptionPhoneEnabled: false,
            subscriptionPhoneRequired: false,
            subscriptionNameEnabled: false,
            subscriptionNameRequired: false,
            subscriptionLabels: [],
        },
    });
    const links = watch('links');

    const allLinksHaveValue = useMemo(
        () =>
            Object.values(links)
                .flat()
                .every(link => !!link.title && (!!link.link || !!link.surveyId || !!link.fileUrl)),
        [links],
    );

    useEffect(() => {
        if (selectedMicrosite) {
            setValue('name', selectedMicrosite.name, { shouldValidate: true });
            setValue('locationId', selectedMicrosite.locationId);
            setValue('slug', selectedMicrosite.slug, { shouldValidate: true });
            setValue('socialMediaEnable', selectedMicrosite.socialMediaEnable);
            setValue('logoUrl', selectedMicrosite.logoUrl);
            setValue('links', selectedMicrosite.links || {});
            setValue('showReviews', selectedMicrosite.showReviews);
            setValue('showLiveChat', !!selectedMicrosite.liveChatChannelId, { shouldValidate: true });
            setValue('liveChatChannelId', selectedMicrosite.liveChatChannelId || '', { shouldValidate: true });
            setValue('subscriptionEnabled', selectedMicrosite.subscriptionEnabled);
            setValue('subscriptionLabels', selectedMicrosite.subscriptionLabels || []);
            setValue('subscriptionEmailEnabled', selectedMicrosite.subscriptionEmailEnabled, {
                shouldValidate: true,
            });
            setValue('subscriptionEmailRequired', selectedMicrosite.subscriptionEmailRequired, {
                shouldValidate: true,
            });
            setValue('subscriptionPhoneEnabled', selectedMicrosite.subscriptionPhoneEnabled, {
                shouldValidate: true,
            });
            setValue('subscriptionPhoneRequired', selectedMicrosite.subscriptionPhoneRequired, {
                shouldValidate: true,
            });
            setValue('subscriptionNameEnabled', selectedMicrosite.subscriptionNameEnabled);
            setValue('subscriptionNameRequired', selectedMicrosite.subscriptionNameRequired);
        }
    }, [selectedMicrosite, setValue]);

    const isView = modal.type === VIEW_TYPE.VIEW;

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={t('MicrositesPage.modalTitle')}
            onPrimaryAction={isUser ? null : handleSubmit(onSave)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty || !allLinksHaveValue}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={isUser ? null : onDelete}
            additionalTextButton={t('buttons.deleteSite')}
        >
            <FormProvider {...{ setValue, control, isValid, watch, getValues, errors }}>
                <FormWrapper>
                    <RadioButtonsWrapper>
                        <RadioButtons
                            value={tab}
                            onChange={setTab}
                            options={[
                                { value: Tab.Settings, label: t('common.settings') },
                                { value: Tab.Content, label: t('MicrositesPage.content') },
                            ]}
                        />
                    </RadioButtonsWrapper>
                    {tab === Tab.Settings && <General isView={isView} selectedMicrosite={selectedMicrosite} />}
                    {tab === Tab.Content && (
                        <Landing isView={isView} locale={account?.locale} selectedMicrosite={selectedMicrosite} />
                    )}
                </FormWrapper>
                <PreviewWrapper>
                    <PreviewMicrosite slug={selectedMicrosite?.slug} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
